import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { FlaskConical, Plus, Trash2, Loader2, Download, Send, History, MessageSquare, FileUp, FileText, X, RotateCcw, Star, Copy as CopyIcon, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import BackButton from "../components/BackButton";
import CopyButton from "../components/CopyButton";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ResizableSplit from "../components/ResizableSplit";
import { normalizeMarkdown } from "../utils/normalizeMarkdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILE_ACCEPT = ".pdf,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.txt,.csv";
const MAX_FILES = 50;

const OPTIONS = {
  elastomers: ["Natural Rubber (NR)", "Styrene-Butadiene (SBR)", "Nitrile Butadiene (NBR)", "Hydrogenated NBR (HNBR)", "EPDM", "Epichlorohydrin Rubber (ECO)", "Chloroprene Rubber (CR)", "Silicone Rubber", "LSR", "Butyl Rubber (IIR)", "Halobutyl Rubber (CIIR, BIIR)", "Polyurethane (PU)", "TPE", "Fluoroelastomers (FKM)"],
  fillers: ["Carbon Black", "Silica", "Calcium Carbonate", "Talc", "Aluminium Trihydrate (ATH)"],
  activators: ["Zinc Oxide (ZnO)", "Stearic Acid", "Magnesium Oxide (MgO)", "Calcium Oxide (CaO)", "Triallyl Isocyanurate (TAIC)", "Triallyl Cyanurate (TAC)", "Bisphenol", "Platinum Catalyst"],
  processing_aids: ["Wax", "Zinc Stearate", "DOP", "DBP", "Resins", "Silicone Oil", "Processing Oils"],
  accelerators: ["Sulphenamides (CBS, MBS, TBBS, DCBS)", "Thiazoles (MBT, MBTS, ZMBT)", "Thiurams (TMTM, TMTD, TBzTD, TETD)", "Dithiocarbamates (ZDEC, ZBEC, ZDBC, ZDMC)", "Guanidines (DPG, DOTG)", "Xanthates (ZIX)", "Thiourea (ETU, DETU)"],
  curing_system: ["Sulphur", "Peroxide", "Addition - Platinum", "Urethane", "Bisphenol AF"],
};

const newRow = () => ({ name: "", phr: "", custom: false, files: [] });
const hydrateRows = (catKey, arr) =>
  (arr || []).map((r) => ({ name: r.name || "", phr: r.phr || "", custom: !(OPTIONS[catKey] || []).includes(r.name), files: r.files || [] }));

// One ingredient row: dropdown (or free text) + phr + TDS/SDS upload + delete + uploaded-file chips
const IngredientRow = ({ row, options, onChange, onRemove, onUpload, busyKind, testid }) => {
  const inList = options && options.includes(row.name);
  const showSelect = options && !row.custom && (inList || !row.name);
  return (
    <div className="space-y-1" data-testid={`${testid}-row`}>
      <div className="flex gap-1.5 items-center">
        {showSelect ? (
          <select
            value={inList ? row.name : ""}
            onChange={(e) => {
              if (e.target.value === "__other__") onChange({ custom: true, name: "" });
              else onChange({ name: e.target.value });
            }}
            className="flex-1 min-w-0 h-9 text-sm rounded-md border border-slate-200 bg-white px-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-300"
            data-testid={`${testid}-select`}
          >
            <option value="" disabled>Select…</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
            <option value="__other__">Others — type your own</option>
          </select>
        ) : (
          <div className="flex-1 min-w-0 flex gap-1 items-center">
            <Input value={row.name} onChange={(e) => onChange({ name: e.target.value })}
              placeholder="Ingredient name / grade" className="flex-1 h-9 text-sm" data-testid={`${testid}-name`} />
            {options && (
              <button type="button" title="Back to dropdown" onClick={() => onChange({ custom: false, name: "" })}
                className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0" data-testid={`${testid}-back-to-select`}>
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        <Input value={row.phr} onChange={(e) => onChange({ phr: e.target.value })}
          placeholder="phr" className="w-16 h-9 text-sm flex-shrink-0" data-testid={`${testid}-phr`} />
        <label className={`inline-flex items-center gap-0.5 px-1.5 h-9 rounded-md cursor-pointer text-[11px] font-semibold border flex-shrink-0 ${busyKind === "tds" ? "bg-amber-50 text-amber-600 border-amber-200 cursor-wait" : "bg-amber-500 hover:bg-amber-600 text-white border-amber-500"}`} title="Upload TDS for this ingredient">
          {busyKind === "tds" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}TDS
          <input type="file" multiple accept={FILE_ACCEPT} className="hidden" disabled={!!busyKind}
            onChange={(e) => onUpload(e, "tds")} data-testid={`${testid}-tds`} />
        </label>
        <label className={`inline-flex items-center gap-0.5 px-1.5 h-9 rounded-md cursor-pointer text-[11px] font-semibold border flex-shrink-0 ${busyKind === "sds" ? "bg-rose-50 text-rose-600 border-rose-200 cursor-wait" : "bg-rose-500 hover:bg-rose-600 text-white border-rose-500"}`} title="Upload SDS for this ingredient">
          {busyKind === "sds" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}SDS
          <input type="file" multiple accept={FILE_ACCEPT} className="hidden" disabled={!!busyKind}
            onChange={(e) => onUpload(e, "sds")} data-testid={`${testid}-sds`} />
        </label>
        <button type="button" onClick={onRemove} className="text-slate-400 hover:text-red-500 p-1 flex-shrink-0" data-testid={`${testid}-remove`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {row.files?.length > 0 && (
        <div className="flex flex-wrap gap-1 pl-1">
          {row.files.map((f) => (
            <span key={f.id} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${f.kind === "sds" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid={`${testid}-file-${f.id}`}>
              <FileText className="w-3 h-3" />
              {f.filename.length > 26 ? f.filename.slice(0, 24) + "…" : f.filename} ({(f.kind || "tds").toUpperCase()})
              <button type="button" title="Remove this uploaded file" onClick={() => onChange({ files: row.files.filter((x) => x.id !== f.id) })}
                className="hover:text-red-600" data-testid={`${testid}-file-remove-${f.id}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {busyKind && (
        <div className="text-[11px] text-blue-600 flex items-center gap-1 pl-1" data-testid={`${testid}-uploading`}>
          <Loader2 className="w-3 h-3 animate-spin" /> Uploading & extracting {busyKind.toUpperCase()}… please wait
        </div>
      )}
    </div>
  );
};

const CategoryCard = ({ title, rows, setRows, options, uploaderFactory, busyRow, testidPrefix }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-semibold text-slate-800">{title}</h4>
      <Button type="button" size="sm" variant="outline" onClick={() => setRows([...rows, newRow()])} data-testid={`${testidPrefix}-add`}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add
      </Button>
    </div>
    <div className="space-y-2">
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-1">No entries — click Add</div>
      ) : (
        rows.map((r, i) => (
          <IngredientRow key={i} row={r} options={options}
            onChange={(patch) => setRows(rows.map((x, idx) => (idx === i ? { ...x, ...patch } : x)))}
            onRemove={() => setRows(rows.filter((_, idx) => idx !== i))}
            onUpload={uploaderFactory(i)}
            busyKind={busyRow && busyRow.key === `${testidPrefix}-${i}` ? busyRow.kind : ""}
            testid={`${testidPrefix}-${i}`} />
        ))
      )}
    </div>
  </div>
);

const CustomCategorySection = ({ title, blocks, setBlocks, makeBlockRowUploader, busyRow, testidPrefix }) => {
  const updateBlock = (bi, patch) => setBlocks(blocks.map((b, idx) => (idx === bi ? { ...b, ...patch } : b)));
  return (
    <div className="bg-orange-50/60 border border-dashed border-orange-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-800">{title}</h4>
        <Button type="button" size="sm" variant="outline" onClick={() => setBlocks([...blocks, { category: "", items: [newRow()] }])} data-testid={`${testidPrefix}-add-block`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Custom Category
        </Button>
      </div>
      {blocks.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Optional: add any custom ingredient category (e.g. Antioxidants, Bonding agents)</div>
      ) : (
        <div className="space-y-3">
          {blocks.map((b, bi) => (
            <div key={bi} className="bg-white rounded-lg border border-slate-200 p-3" data-testid={`${testidPrefix}-block-${bi}`}>
              <div className="flex gap-2 mb-2">
                <Input value={b.category} onChange={(e) => updateBlock(bi, { category: e.target.value })}
                  placeholder="Category name (e.g. Antioxidants, Bonding agents)" className="flex-1 h-9 text-sm font-medium"
                  data-testid={`${testidPrefix}-cat-name-${bi}`} />
                <button type="button" onClick={() => setBlocks(blocks.filter((_, idx) => idx !== bi))}
                  className="text-slate-400 hover:text-red-500 p-1.5" data-testid={`${testidPrefix}-remove-block-${bi}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2">
                {(b.items || []).map((r, ri) => (
                  <IngredientRow key={ri} row={r} options={null}
                    onChange={(patch) => updateBlock(bi, { items: b.items.map((x, idx) => (idx === ri ? { ...x, ...patch } : x)) })}
                    onRemove={() => updateBlock(bi, { items: b.items.filter((_, idx) => idx !== ri) })}
                    onUpload={makeBlockRowUploader(bi, b.category)(ri)}
                    busyKind={busyRow && busyRow.key === `${testidPrefix}-${bi}-${ri}` ? busyRow.kind : ""}
                    testid={`${testidPrefix}-${bi}-${ri}`} />
                ))}
                <Button type="button" size="sm" variant="ghost" onClick={() => updateBlock(bi, { items: [...(b.items || []), newRow()] })}
                  className="text-orange-600" data-testid={`${testidPrefix}-${bi}-add-item`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add item
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ForwardPredictionPage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("designer");
  const [histFilter, setHistFilter] = useState("all");

  const [elastomers, setElastomers] = useState([newRow()]);
  const [fillers, setFillers] = useState([newRow()]);
  const [activators, setActivators] = useState([newRow()]);
  const [processingAids, setProcessingAids] = useState([newRow()]);
  const [section1Custom, setSection1Custom] = useState([]);
  const [accelerators, setAccelerators] = useState([newRow()]);
  const [curingSystem, setCuringSystem] = useState([newRow()]);
  const [section2Custom, setSection2Custom] = useState([]);

  const [application, setApplication] = useState("");
  const [additionalReqs, setAdditionalReqs] = useState("");
  const [result, setResult] = useState("");
  const [resultId, setResultId] = useState(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [docsUsed, setDocsUsed] = useState([]);
  const [carriedAttachmentIds, setCarriedAttachmentIds] = useState([]);
  const [duplicatedFrom, setDuplicatedFrom] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [busyRow, setBusyRow] = useState(null); // { key, kind }

  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/formulation-history`);
      setHistory((r.data || []).filter((x) => x.type === "forward_prediction"));
    } catch (e) {}
  };
  useEffect(() => { loadHistory(); }, []);

  const countFiles = () => {
    const std = [elastomers, fillers, activators, processingAids, accelerators, curingSystem].flat();
    const cus = [...section1Custom, ...section2Custom].flatMap((b) => b.items || []);
    return [...std, ...cus].reduce((n, r) => n + (r.files?.length || 0), 0);
  };

  const doExtract = async (files, form, kind) => {
    const r = await axios.post(`${API}/forward-prediction/extract-tds`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
    });
    const ex = r.data?.extracted || {};
    const atts = (r.data?.attachments || []).map((a) => ({ ...a, kind }));
    return { ex, atts };
  };

  const makeUploader = (catKey, rows, setRows, prefix) => (rowIdx) => async (e, kind) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (countFiles() + files.length > MAX_FILES) {
      toast.error(`Limit: ${MAX_FILES} TDS/SDS files per prediction session`);
      e.target.value = "";
      return;
    }
    setBusyRow({ key: `${prefix}-${rowIdx}`, kind });
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      form.append("category", catKey);
      form.append("kind", kind);
      const { ex, atts } = await doExtract(files, form, kind);
      const arr = (Array.isArray(ex[catKey]) ? ex[catKey] : []).filter((x) => (x.name || "").trim());
      setRows((prev) => {
        const next = [...prev];
        const row = { ...next[rowIdx] };
        row.files = [...(row.files || []), ...atts];
        if (arr.length > 0) {
          row.name = arr[0].name || row.name;
          row.custom = true;
        }
        next[rowIdx] = row;
        return next;
      });
      if (ex.application) setApplication((prev) => prev || ex.application);
      if (arr.length > 0) {
        toast.success(`${kind.toUpperCase()} uploaded ✓ — "${arr[0].name}" filled into this row${ex.supplier ? ` · supplier: ${ex.supplier}` : ""}. Enter your own phr.`);
      } else {
        toast.success(`${kind.toUpperCase()} uploaded ✓ — content stored and will be used in the prediction`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not process the ${kind.toUpperCase()} file`);
    } finally {
      setBusyRow(null);
      e.target.value = "";
    }
  };

  const makeCustomUploader = (blocks, setBlocks, prefix) => (blockIdx, blockName) => (rowIdx) => async (e, kind) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (countFiles() + files.length > MAX_FILES) {
      toast.error(`Limit: ${MAX_FILES} TDS/SDS files per prediction session`);
      e.target.value = "";
      return;
    }
    setBusyRow({ key: `${prefix}-${blockIdx}-${rowIdx}`, kind });
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      form.append("category", "custom");
      form.append("custom_category_name", blockName || "Custom");
      form.append("kind", kind);
      const { ex, atts } = await doExtract(files, form, kind);
      const customArr = (ex.section1_custom || []).concat(ex.section2_custom || []);
      const match = customArr.find((c) => (c.category || "").toLowerCase().trim() === (blockName || "").toLowerCase().trim()) || customArr[0];
      const arr = (match && Array.isArray(match.items) ? match.items : []).filter((x) => (x.name || "").trim());
      setBlocks((prev) => {
        const next = [...prev];
        const block = { ...next[blockIdx] };
        const items = [...(block.items || [])];
        const row = { ...items[rowIdx] };
        row.files = [...(row.files || []), ...atts];
        if (arr.length > 0) {
          row.name = arr[0].name || row.name;
          row.custom = true;
        }
        items[rowIdx] = row;
        block.items = items;
        next[blockIdx] = block;
        return next;
      });
      if (arr.length > 0) toast.success(`${kind.toUpperCase()} uploaded ✓ — "${arr[0].name}" filled into this row. Enter your own phr.`);
      else toast.success(`${kind.toUpperCase()} uploaded ✓ — content stored and will be used in the prediction`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not process the ${kind.toUpperCase()} file`);
    } finally {
      setBusyRow(null);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = (arr) => arr.filter((x) => (x.name || "").trim()).map(({ name, phr }) => ({ name, phr }));
    const cleanBlocks = (arr) =>
      arr.map((b) => ({ category: b.category, items: clean(b.items || []) }))
        .filter((b) => (b.category || "").trim() && b.items.length > 0);

    const allRows = [
      ...elastomers, ...fillers, ...activators, ...processingAids, ...accelerators, ...curingSystem,
      ...section1Custom.flatMap((b) => b.items || []),
      ...section2Custom.flatMap((b) => b.items || []),
    ];
    const rowAttachmentIds = allRows.flatMap((r) => (r.files || []).map((f) => f.id));
    const attachment_ids = [...new Set([...rowAttachmentIds, ...carriedAttachmentIds])];

    const payload = {
      elastomers: clean(elastomers),
      fillers: clean(fillers),
      activators: clean(activators),
      processing_aids: clean(processingAids),
      section1_custom: cleanBlocks(section1Custom),
      accelerators: clean(accelerators),
      curing_system: clean(curingSystem),
      section2_custom: cleanBlocks(section2Custom),
      application: application || undefined,
      additional_requirements: additionalReqs || undefined,
      language: (i18n.language || "en").startsWith("de") ? "de" : "en",
      attachment_ids,
    };

    if (payload.elastomers.length === 0) {
      toast.error("Please add at least one elastomer");
      return;
    }

    setLoading(true);
    setResult("");
    setNeedsConfirmation(false);
    setDocsUsed([]);
    setResultId(null);
    setChatMsgs([]);
    try {
      const r = await axios.post(`${API}/forward-prediction`, payload);
      setResult(r.data.response);
      setResultId(r.data.id);
      setNeedsConfirmation(!!r.data.needs_confirmation);
      setDocsUsed(r.data.attachments_used || []);
      loadHistory();
      toast.info("Review what the model understood, then confirm below to run the prediction");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Prediction failed — please retry");
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    if (!resultId) return;
    setExporting(true);
    try {
      const r = await axios.get(`${API}/formulation/${resultId}/export-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `forward-prediction-${resultId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const deleteResult = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this prediction?")) return;
    try {
      await axios.delete(`${API}/formulation-history/${id}`);
      toast.success("Deleted");
      if (id === resultId) { setResult(""); setResultId(null); setChatMsgs([]); }
      loadHistory();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleFavorite = async (f) => {
    try {
      const r = await axios.patch(`${API}/formulation-history/${f.id}/favorite`);
      setHistory((h) => h.map((x) => (x.id === f.id ? { ...x, is_favorite: r.data.is_favorite } : x)));
    } catch {}
  };

  const applyInputs = (f, { asNewSession }) => {
    const inp = f.inputs || {};
    setElastomers(inp.elastomers?.length ? hydrateRows("elastomers", inp.elastomers) : [newRow()]);
    setFillers(inp.fillers?.length ? hydrateRows("fillers", inp.fillers) : [newRow()]);
    setActivators(inp.activators?.length ? hydrateRows("activators", inp.activators) : [newRow()]);
    setProcessingAids(inp.processing_aids?.length ? hydrateRows("processing_aids", inp.processing_aids) : [newRow()]);
    setSection1Custom((inp.section1_custom || []).map((b) => ({ ...b, items: hydrateRows("custom", b.items) })));
    setAccelerators(inp.accelerators?.length ? hydrateRows("accelerators", inp.accelerators) : [newRow()]);
    setCuringSystem(inp.curing_system?.length ? hydrateRows("curing_system", inp.curing_system) : [newRow()]);
    setSection2Custom((inp.section2_custom || []).map((b) => ({ ...b, items: hydrateRows("custom", b.items) })));
    setApplication(inp.application || "");
    setAdditionalReqs(inp.additional_requirements || "");
    setCarriedAttachmentIds(inp.attachment_ids || []);
    if (asNewSession) {
      setResult("");
      setResultId(null);
      setChatMsgs([]);
      setNeedsConfirmation(false);
      setDocsUsed(f.attachments_used || []);
      setDuplicatedFrom(f.title || "previous session");
    } else {
      setResult(f.response);
      setResultId(f.id);
      setNeedsConfirmation(f.stage === "awaiting_confirmation");
      setChatMsgs(f.chat_messages || []);
      setDocsUsed(f.attachments_used || []);
      setDuplicatedFrom("");
    }
    setActiveTab("designer");
  };

  const loadFromHistory = (f) => applyInputs(f, { asNewSession: false });

  const duplicateFromHistory = (f) => {
    applyInputs(f, { asNewSession: true });
    const n = (f.inputs?.attachment_ids || []).length;
    toast.success(`Recipe copied into a NEW session${n ? ` — ${n} TDS/SDS document${n > 1 ? "s" : ""} carried over` : ""}. Change anything and press Predict.`);
  };

  const sendChatMsg = async () => {
    if (!chatInput.trim() || !resultId) return;
    const userMsg = { role: "user", content: chatInput };
    setChatMsgs((m) => [...m, userMsg]);
    setChatInput("");
    setChatLoading(true);
    try {
      const r = await axios.post(`${API}/formulation/${resultId}/chat`, {
        formulation_id: resultId,
        message: userMsg.content,
        language: (i18n.language || "en").startsWith("de") ? "de" : "en",
      });
      if (r.data.prediction_ready) {
        setResult(r.data.response);
        setNeedsConfirmation(false);
        setChatMsgs((m) => [...m, { role: "assistant", content: "Prediction generated — see the full report above." }]);
        toast.success("Prediction complete");
        loadHistory();
      } else {
        setChatMsgs((m) => [...m, { role: "assistant", content: r.data.response }]);
      }
    } catch {
      setChatMsgs((m) => [...m, { role: "assistant", content: "Error — please retry" }]);
    } finally {
      setChatLoading(false);
    }
  };

  const visibleHistory = histFilter === "fav" ? history.filter((f) => f.is_favorite) : history;

  const leftPanel = (
    <form onSubmit={submit} className="space-y-5 lg:sticky lg:top-0 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto pr-1">
      {duplicatedFrom && (
        <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="fwd-duplicated-banner">
          <CopyIcon className="w-3.5 h-3.5 flex-shrink-0" />
          Copied from "{duplicatedFrom}" — edit anything and press Predict to create a new saved session.
          {carriedAttachmentIds.length > 0 && <span className="font-medium">({carriedAttachmentIds.length} TDS/SDS carried over)</span>}
        </div>
      )}
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1 block">Application (optional but helps prediction)</label>
        <Input value={application} onChange={(e) => setApplication(e.target.value)}
          placeholder="e.g. automotive weather seal, tire tread, conveyor belt cover"
          className="bg-white border-slate-200" data-testid="fwd-application" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1 block">Additional requirements (optional)</label>
        <Textarea value={additionalReqs} onChange={(e) => setAdditionalReqs(e.target.value)}
          placeholder="e.g. must be FDA compliant, low odor, colorable, ozone resistant, target cost..."
          rows={2} className="bg-white border-slate-200 resize-none" data-testid="fwd-additional-reqs" />
      </div>

      <div className="border-l-4 border-orange-400 pl-3">
        <h3 className="font-heading font-bold text-slate-900 text-sm uppercase tracking-wide">Section 1 — Base Compound</h3>
      </div>
      <CategoryCard title="Elastomers" rows={elastomers} setRows={setElastomers} options={OPTIONS.elastomers}
        uploaderFactory={makeUploader("elastomers", elastomers, setElastomers, "elastomers")} busyRow={busyRow} testidPrefix="elastomers" />
      <CategoryCard title="Fillers" rows={fillers} setRows={setFillers} options={OPTIONS.fillers}
        uploaderFactory={makeUploader("fillers", fillers, setFillers, "fillers")} busyRow={busyRow} testidPrefix="fillers" />
      <CategoryCard title="Activators" rows={activators} setRows={setActivators} options={OPTIONS.activators}
        uploaderFactory={makeUploader("activators", activators, setActivators, "activators")} busyRow={busyRow} testidPrefix="activators" />
      <CategoryCard title="Processing Aids" rows={processingAids} setRows={setProcessingAids} options={OPTIONS.processing_aids}
        uploaderFactory={makeUploader("processing_aids", processingAids, setProcessingAids, "processing-aids")} busyRow={busyRow} testidPrefix="processing-aids" />
      <CustomCategorySection title="+ Additional Section 1 Categories" blocks={section1Custom} setBlocks={setSection1Custom}
        makeBlockRowUploader={makeCustomUploader(section1Custom, setSection1Custom, "sec1-custom")} busyRow={busyRow} testidPrefix="sec1-custom" />

      <div className="border-l-4 border-blue-400 pl-3 pt-2">
        <h3 className="font-heading font-bold text-slate-900 text-sm uppercase tracking-wide">Section 2 — Cure System</h3>
      </div>
      <CategoryCard title="Accelerators" rows={accelerators} setRows={setAccelerators} options={OPTIONS.accelerators}
        uploaderFactory={makeUploader("accelerators", accelerators, setAccelerators, "accelerators")} busyRow={busyRow} testidPrefix="accelerators" />
      <CategoryCard title="Curing System" rows={curingSystem} setRows={setCuringSystem} options={OPTIONS.curing_system}
        uploaderFactory={makeUploader("curing_system", curingSystem, setCuringSystem, "curing-system")} busyRow={busyRow} testidPrefix="curing-system" />
      <CustomCategorySection title="+ Additional Section 2 Categories" blocks={section2Custom} setBlocks={setSection2Custom}
        makeBlockRowUploader={makeCustomUploader(section2Custom, setSection2Custom, "sec2-custom")} busyRow={busyRow} testidPrefix="sec2-custom" />

      <div className="text-[11px] text-slate-500 px-1">
        {countFiles()} / {MAX_FILES} TDS/SDS files attached this session — every uploaded file's full content is read and used in the prediction.
      </div>

      <Button type="submit" disabled={loading || !!busyRow} data-testid="fwd-predict-btn"
        className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-medium">
        {loading ? (<><Loader2 className="w-5 h-5 animate-spin mr-2" /> Predicting...</>) : (<><FlaskConical className="w-5 h-5 mr-2" /> Predict Properties</>)}
      </Button>
    </form>
  );

  const rightPanel = (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col lg:sticky lg:top-0 lg:h-[calc(100vh-7rem)]">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className="font-heading font-bold text-slate-900">Predicted Output</h2>
        <div className="flex items-center gap-2">
          {result && <CopyButton text={result} label />}
          {resultId && !needsConfirmation && (
            <Button variant="outline" size="sm" onClick={exportPdf} disabled={exporting} data-testid="fwd-export-pdf">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              Export PDF
            </Button>
          )}
          {resultId && (
            <Button variant="ghost" size="sm" onClick={() => deleteResult(resultId)} className="text-red-500" data-testid="fwd-delete">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pr-2 -mr-2">
        {docsUsed.length > 0 && (result || loading) && (
          <div className="mb-3 text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="fwd-docs-used">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-green-600 mt-0.5" />
            <span>
              <strong>{docsUsed.length} TDS/SDS document{docsUsed.length > 1 ? "s" : ""} read & used:</strong>{" "}
              {docsUsed.join(" · ")}
            </span>
          </div>
        )}
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-4" />
            <p className="text-slate-500">Reading your recipe{countFiles() + carriedAttachmentIds.length > 0 ? " and every uploaded document" : ""}...</p>
          </div>
        ) : result ? (
          <div className="prose prose-sm max-w-none break-words prose-p:my-3 prose-headings:mt-5 prose-headings:mb-2 prose-h2:text-base prose-h2:font-semibold prose-ul:my-3 prose-ul:pl-5 prose-li:my-1 prose-strong:text-slate-900 prose-strong:font-semibold prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none" data-testid="fwd-result">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                table: (p) => (
                  <div className="overflow-x-auto my-3 -mx-1 px-1">
                    <table className="w-full border-collapse text-xs" {...p} />
                  </div>
                ),
                th: (p) => <th className="border border-slate-300 px-2 py-1.5 bg-slate-100 text-left font-semibold" {...p} />,
                td: (p) => <td className="border border-slate-300 px-2 py-1.5 align-top" {...p} />,
              }}
            >
              {normalizeMarkdown(result)}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <FlaskConical className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-slate-500">Fill the recipe on the left and click <strong>Predict</strong></p>
          </div>
        )}

        {resultId && chatMsgs.length > 0 && (
          <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Conversation
            </h3>
            {chatMsgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} group`}>
                <div className={`px-3 py-2 rounded-lg text-sm ${m.role === "user" ? "bg-slate-900 text-white max-w-[85%]" : "bg-slate-100 text-slate-900 max-w-full w-full"}`}>
                  {m.role === "assistant" ? (
                    <div className="prose prose-sm max-w-none break-words prose-p:my-3">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizeMarkdown(m.content)}</ReactMarkdown>
                    </div>
                  ) : (
                    <div>{m.content}</div>
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="text-xs text-slate-500"><Loader2 className="w-3 h-3 inline animate-spin" /> Thinking...</div>
            )}
          </div>
        )}
      </div>

      {resultId && (
        <div className="mt-4 pt-4 border-t border-slate-200 flex-shrink-0">
          {needsConfirmation && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2" data-testid="fwd-confirm-banner">
              The model has explained what it understood above. Reply <strong>"yes"</strong> to run the prediction, or type any corrections — then it will predict.
            </div>
          )}
          <div className="flex gap-2 items-end">
            <Textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChatMsg(); }
              }}
              placeholder={needsConfirmation ? 'Type "yes" to confirm, or your corrections... (Enter to send)' : "Ask follow-ups about this prediction... (Enter to send, Shift+Enter for new line)"}
              rows={1}
              className="bg-slate-50 border-slate-200 resize-none min-h-[44px] max-h-32 py-2.5"
              data-testid="fwd-chat-input"
            />
            <Button size="sm" onClick={sendChatMsg} disabled={chatLoading || !chatInput.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white h-11 px-4" data-testid="fwd-chat-send">
              {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3">
          <BackButton />
          <FlaskConical className="w-5 h-5 text-orange-500 ml-2" />
          <h1 className="font-heading font-semibold text-slate-900">{t("dashboard.forwardPrediction")}</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">Ingredients → Properties</span>
          <div className="flex-1" />
          <LanguageSwitcher />
          <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1 ml-2">
            <button onClick={() => setActiveTab("designer")} data-testid="fwd-tab-designer"
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${activeTab === "designer" ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}>
              Designer
            </button>
            <button onClick={() => setActiveTab("history")} data-testid="fwd-tab-history"
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${activeTab === "history" ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}>
              <History className="w-3.5 h-3.5 mr-1 inline" /> {t("formulation.history")} ({history.length})
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {activeTab === "history" ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-heading text-xl font-bold text-slate-900">Saved Forward Predictions</h2>
                  <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
                    <button onClick={() => setHistFilter("all")} data-testid="fwd-hist-filter-all"
                      className={`px-3 py-1 rounded-full text-xs font-medium ${histFilter === "all" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}>
                      All ({history.length})
                    </button>
                    <button onClick={() => setHistFilter("fav")} data-testid="fwd-hist-filter-fav"
                      className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${histFilter === "fav" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}>
                      <Star className="w-3 h-3 text-amber-500 fill-amber-500" /> Favorites ({history.filter((f) => f.is_favorite).length})
                    </button>
                  </div>
                </div>
                {visibleHistory.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    {histFilter === "fav" ? "No favorites yet — click the star on any saved prediction" : "No predictions yet — go to Designer to start"}
                  </div>
                ) : (
                  visibleHistory.map((f) => (
                    <div key={f.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 hover:border-orange-300 transition-colors" data-testid={`fwd-history-${f.id}`}>
                      <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                        <FlaskConical className="w-6 h-6 text-orange-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{f.title || "Forward Prediction"}</p>
                        <p className="text-xs text-slate-500">{new Date(f.created_at).toLocaleString()}</p>
                      </div>
                      <button onClick={() => toggleFavorite(f)} data-testid={`fwd-fav-${f.id}`}
                        title={f.is_favorite ? "Remove from favorites" : "Mark as favorite"}
                        className="p-2 rounded-lg hover:bg-amber-50">
                        <Star className={`w-5 h-5 ${f.is_favorite ? "text-amber-500 fill-amber-500" : "text-slate-300"}`} />
                      </button>
                      <Button size="sm" variant="outline" onClick={() => duplicateFromHistory(f)} data-testid={`fwd-duplicate-${f.id}`}
                        title="Copy this whole recipe (incl. uploaded documents) into a new session">
                        <CopyIcon className="w-3.5 h-3.5 mr-1" /> Duplicate & edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => loadFromHistory(f)} data-testid={`fwd-load-${f.id}`}>Load</Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteResult(f.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <ResizableSplit storageKey="fwd-split">
                {leftPanel}
                {rightPanel}
              </ResizableSplit>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
